/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomTextFieldN.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 28-06-2024
 * @description Reusable text field component.
 */

import { Box, TextField } from "@mui/material";
import React from "react";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 28-06-2024
 * @description Reusable text field component.
 * @param : size, label, placeholder, value, setValue, isMultiline = false, rows = 1, txtFldStyle
 * @return The rendered text field component.
 */
const CustomTextFieldN = ({
  size,
  label,
  type,
  fldType,
  placeholder,
  required,
  value,
  setValue,
  variant,
  valueError,
  setValueError = () => null,
  inputProps,
  disabled,
  isMultiline = false,
  minRows = 1,
  maxRows = 3,
  txtFldStyle,
}) => {
  const regexMin3 = /^(?=.*[a-zA-Z0-9._-])[a-zA-Z0-9._-]{3,}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateMinLength = (value, min) => {
    return value?.trim()?.length < min
      ? `Minimum ${min} characters required`
      : "";
  };

  const handleTextR = (value) => {
    const noSpaceValue = value.replace(/\s+/g, "");

    if (noSpaceValue?.trim()?.length < 3) {
      return "Minimum 3 characters required";
    }

    if (!regexMin3.test(noSpaceValue)) {
      return "Invalid format";
    }

    return "";
  };

  const handleTextO = (value) => {
    const len = value?.trim()?.length;

    if (len > 0 && len < 3) {
      return "Minimum 3 characters required";
    }

    return "";
  };

  const handleEmail = (value) => {
    return emailRegex.test(value) ? "" : "Invalid email address";
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    // Remove leading spaces
    newValue = newValue.replace(/^\s+/, "");
    // Normalize trailing space
    newValue = newValue.replace(/\s+$/, " ");

    let error = "";

    switch (fldType) {
      case "text1":
        error = validateMinLength(newValue, 1);
        break;

      case "text":
        error = validateMinLength(newValue, 3);
        break;

      case "textR":
        error = handleTextR(newValue);
        newValue = newValue.replace(/\s+/g, ""); // keep original behavior
        break;

      case "textO":
        error = handleTextO(newValue);
        break;

      case "text4":
        error = validateMinLength(newValue, 4);
        break;

      case "text5":
        error = validateMinLength(newValue, 5);
        break;

      case "email":
        error = handleEmail(newValue);
        break;

      default:
        break;
    }

    setValueError(error);
    setValue(newValue);
  };

  return (
    <Box>
      <TextField
        fullWidth
        size={size}
        label={label}
        type={type}
        error={Boolean(valueError)}
        helperText={valueError}
        variant={variant}
        placeholder={placeholder}
        value={value}
        InputProps={inputProps}
        onChange={handleChange}
        multiline={isMultiline}
        minRows={minRows}
        maxRows={maxRows}
        sx={txtFldStyle}
        disabled={disabled}
      />
    </Box>
  );
};

export default CustomTextFieldN;

// Styling for the CustomTextField component
const styles = {
  txtLbs: {
    textAlign: "start",
    mt: 1,
    mb: 0.5,
    pl: 0.5,
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "black",
    },
  },
};
